import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    AutoAwesome as AiIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { IGeneratedQuestionnaireDraft } from '../services/questionnaireService';
import GenerateQuestionnaireModal from '../components/GenerateQuestionnaireModal';
import { stitchColors } from '../theme/tokens';

// Types matching backend
interface Field {
    id: string;
    label: string;
    type: string;
    required: boolean;
    helperText?: string;
    options?: string[];
    placeholder?: string;
    aiHelper?: string;
    tooltip?: string;
    questionAppearance?: string;
    financialCategory?: 'asset' | 'liability' | 'none';
}

interface StepDef {
    id?: string;
    section: string;
    title: string;
    description?: string;
    fields: Field[];
}

interface QuestionnaireDefinition {
    version: number;
    steps: StepDef[];
    isActive: boolean;
}

const emptyDefinition = (): QuestionnaireDefinition => ({
    version: 0,
    steps: [],
    isActive: true,
});

const AdminWelcomeQuestionnaire: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [definition, setDefinition] = useState<QuestionnaireDefinition>(emptyDefinition());
    const [expandedStep, setExpandedStep] = useState<number | false>(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [generateModalOpen, setGenerateModalOpen] = useState(false);
    const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
    const [pendingDraft, setPendingDraft] = useState<IGeneratedQuestionnaireDraft | null>(null);

    const applyDraft = (draft: IGeneratedQuestionnaireDraft) => {
        setDefinition(prev => ({
            ...prev,
            steps: draft.steps.map(step => ({
                id: step.id,
                section: step.section || 'General',
                title: step.title,
                description: step.description,
                fields: step.fields.map(f => ({
                    id: f.id,
                    label: f.label,
                    type: f.type,
                    required: f.required,
                    options: f.options,
                    placeholder: f.placeholder,
                    aiHelper: f.aiHelper,
                    tooltip: f.tooltip,
                    questionAppearance: f.questionAppearance,
                    financialCategory: f.financialCategory || 'none',
                })),
            })),
        }));
        setSuccess(`Successfully generated ${draft.steps.length} step(s) with AI! Review and click Save Changes.`);
        setError('');
        setPendingDraft(null);
        setConfirmOverwriteOpen(false);
    };

    const handleGeneratedDraft = (draft: IGeneratedQuestionnaireDraft) => {
        if (definition.steps.length > 0) {
            setPendingDraft(draft);
            setConfirmOverwriteOpen(true);
        } else {
            applyDraft(draft);
        }
    };

    const {
        data,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['welcomeQuestionnaire', 'adminDefinition'],
        queryFn: async () => {
            try {
                const res = await api.get<QuestionnaireDefinition>('/welcome-questionnaire/definition/admin');
                return res.data;
            } catch (err: any) {
                if (err.response?.status === 404) {
                    return emptyDefinition();
                }
                throw err;
            }
        },
        staleTime: 5 * 60 * 1000,
    });

    React.useEffect(() => {
        if (data) {
            setDefinition({
                ...data,
                steps: data.steps || [],
            });
        }
    }, [data]);

    const saveMutation = useMutation({
        mutationFn: (payload: QuestionnaireDefinition) =>
            api.post<QuestionnaireDefinition>('/welcome-questionnaire/definition', payload),
        onSuccess: (res) => {
            setDefinition(res.data);
            queryClient.setQueryData(['welcomeQuestionnaire', 'adminDefinition'], res.data);
            setSuccess('Questionnaire definition updated successfully!');
            setError('');
        },
        onError: (err: any) => {
            setError(err.response?.data?.error || 'Failed to update definition');
            setSuccess('');
        },
    });

    const handleSave = () => {
        saveMutation.mutate(definition);
    };

    const addStep = () => {
        setDefinition(prev => ({
            ...prev,
            steps: [...prev.steps, { title: 'New Step', section: 'Page 1', fields: [] }],
        }));
        setExpandedStep(definition.steps.length);
    };

    const removeStep = (index: number) => {
        setDefinition(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index),
        }));
        setExpandedStep(false);
    };

    const updateStepTitle = (index: number, title: string) => {
        const newSteps = [...definition.steps];
        newSteps[index].title = title;
        setDefinition(prev => ({ ...prev, steps: newSteps }));
    };

    const generateSemanticId = (section: string, label: string): string => {
        const sectionPart = section.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        const labelPart = label.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        return `${sectionPart}_${labelPart}`.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    };

    const addField = (stepIndex: number) => {
        const newSteps = [...definition.steps];
        const stepTitle = newSteps[stepIndex].title || 'general';
        const defaultLabel = 'New Field';
        const semanticId = generateSemanticId(stepTitle, defaultLabel);
        newSteps[stepIndex].fields.push({
            id: semanticId,
            label: defaultLabel,
            type: 'text',
            required: true,
            financialCategory: 'none',
        });
        setDefinition(prev => ({ ...prev, steps: newSteps }));
    };

    const removeField = (stepIndex: number, fieldIndex: number) => {
        const newSteps = [...definition.steps];
        newSteps[stepIndex].fields.splice(fieldIndex, 1);
        setDefinition(prev => ({ ...prev, steps: newSteps }));
    };

    const updateField = (stepIndex: number, fieldIndex: number, updates: Partial<Field>) => {
        const newSteps = [...definition.steps];
        const currentField = newSteps[stepIndex].fields[fieldIndex];
        const stepTitle = newSteps[stepIndex].title || 'general';

        if (updates.label !== undefined) {
            const currentAutoId = generateSemanticId(stepTitle, currentField.label);
            const newAutoId = generateSemanticId(stepTitle, updates.label);
            if (currentField.id === currentAutoId || currentField.id.startsWith('field_')) {
                updates.id = newAutoId;
            }
        }

        newSteps[stepIndex].fields[fieldIndex] = { ...currentField, ...updates };
        setDefinition(prev => ({ ...prev, steps: newSteps }));
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ m: 3 }}>
                Failed to load questionnaire definition. Please refresh the page.
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Manage Welcome Questionnaire</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AiIcon />}
                        onClick={() => setGenerateModalOpen(true)}
                    >
                        Generate from File (AI)
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate('/admin/welcome-questionnaire/preview')}
                        disabled={definition.steps.length === 0}
                    >
                        View Questionnaire
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saveMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saveMutation.isPending}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ mr: 2 }}>Settings</Typography>
                    <Chip label={`Version: ${definition.version}`} variant="outlined" sx={{ mr: 2 }} />
                    <Chip label={`${definition.steps.length} steps`} variant="outlined" sx={{ mr: 2 }} />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={definition.isActive}
                                onChange={(e) => setDefinition(prev => ({ ...prev, isActive: e.target.checked }))}
                            />
                        }
                        label="Is Active"
                    />
                </Box>
            </Paper>

            <Box>
                {definition.steps.map((step, stepIndex) => (
                    <Accordion
                        key={stepIndex}
                        expanded={expandedStep === stepIndex}
                        onChange={(_, isExpanded) => setExpandedStep(isExpanded ? stepIndex : false)}
                        disableGutters
                        elevation={0}
                        sx={{
                            mb: 2,
                            border: `1px solid ${stitchColors.outlineVariant}`,
                            borderRadius: '8px !important',
                            '&:before': { display: 'none' },
                            overflow: 'hidden',
                        }}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ flexShrink: 0 }}>
                                    Step {stepIndex + 1}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                                    {step.section || 'General'} — {step.title}
                                </Typography>
                                <Chip label={`${step.fields.length} fields`} size="small" variant="outlined" />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pb: 2, borderTop: `1px solid ${stitchColors.outlineVariant}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                <TextField
                                    label="Section (Page Name)"
                                    value={step.section || 'General'}
                                    onChange={(e) => {
                                        const newSteps = [...definition.steps];
                                        newSteps[stepIndex].section = e.target.value;
                                        setDefinition(prev => ({ ...prev, steps: newSteps }));
                                    }}
                                    size="small"
                                    sx={{ width: 200 }}
                                />
                                <TextField
                                    label="Step Title"
                                    value={step.title}
                                    onChange={(e) => updateStepTitle(stepIndex, e.target.value)}
                                    size="small"
                                    sx={{ width: 300 }}
                                />
                                <TextField
                                    label="Step Description (optional)"
                                    value={step.description || ''}
                                    onChange={(e) => {
                                        const newSteps = [...definition.steps];
                                        newSteps[stepIndex].description = e.target.value;
                                        setDefinition(prev => ({ ...prev, steps: newSteps }));
                                    }}
                                    size="small"
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                                <IconButton onClick={() => removeStep(stepIndex)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ pl: { xs: 0, sm: 1 } }}>
                                {step.fields.map((field, fieldIndex) => (
                                    <Paper key={fieldIndex} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: stitchColors.surfaceContainerLowest }}>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'start' }}>
                                            <TextField
                                                label="Field ID (unique key)"
                                                value={field.id}
                                                onChange={(e) => updateField(stepIndex, fieldIndex, { id: e.target.value })}
                                                size="small"
                                            />
                                            <TextField
                                                label="Label"
                                                value={field.label}
                                                onChange={(e) => updateField(stepIndex, fieldIndex, { label: e.target.value })}
                                                size="small"
                                                fullWidth
                                                sx={{ flex: 1, minWidth: 200 }}
                                            />
                                            <FormControl size="small" sx={{ width: 130 }}>
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={field.type}
                                                    label="Type"
                                                    onChange={(e) => updateField(stepIndex, fieldIndex, { type: e.target.value })}
                                                >
                                                    <MenuItem value="text">Text</MenuItem>
                                                    <MenuItem value="textarea">Textarea</MenuItem>
                                                    <MenuItem value="number">Number</MenuItem>
                                                    <MenuItem value="date">Date</MenuItem>
                                                    <MenuItem value="email">Email</MenuItem>
                                                    <MenuItem value="select">Select</MenuItem>
                                                    <MenuItem value="dropdown">Dropdown</MenuItem>
                                                    <MenuItem value="checkbox">Checkbox</MenuItem>
                                                    <MenuItem value="display">Display Text</MenuItem>
                                                    <MenuItem value="file">File Upload</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ width: 130 }}>
                                                <InputLabel>Financial</InputLabel>
                                                <Select
                                                    value={field.financialCategory || 'none'}
                                                    label="Financial"
                                                    onChange={(e) => updateField(stepIndex, fieldIndex, { financialCategory: e.target.value as any })}
                                                >
                                                    <MenuItem value="none">None</MenuItem>
                                                    <MenuItem value="asset">Asset</MenuItem>
                                                    <MenuItem value="liability">Liability</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.required}
                                                        onChange={(e) => updateField(stepIndex, fieldIndex, { required: e.target.checked })}
                                                        size="small"
                                                    />
                                                }
                                                label="Required"
                                            />
                                            <IconButton onClick={() => removeField(stepIndex, fieldIndex)} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>

                                        {(field.type === 'select' || field.type === 'dropdown' || field.type === 'checkbox') && (
                                            <Box sx={{ mt: 2 }}>
                                                <TextField
                                                    label="Options (comma-separated)"
                                                    value={(field.options || []).join(', ')}
                                                    onChange={(e) => updateField(stepIndex, fieldIndex, {
                                                        options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                                    })}
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Option 1, Option 2, Option 3..."
                                                    helperText="Enter options separated by commas"
                                                />
                                            </Box>
                                        )}

                                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <TextField
                                                label="Tooltip"
                                                value={field.tooltip || ''}
                                                onChange={(e) => updateField(stepIndex, fieldIndex, { tooltip: e.target.value })}
                                                size="small"
                                                fullWidth
                                                placeholder="Tooltip text..."
                                                sx={{ flex: 1, minWidth: 200 }}
                                            />
                                            <TextField
                                                label="AI Helper Text"
                                                value={field.aiHelper || ''}
                                                onChange={(e) => updateField(stepIndex, fieldIndex, { aiHelper: e.target.value })}
                                                size="small"
                                                fullWidth
                                                placeholder="Tip needed for AI..."
                                                sx={{ flex: 1, minWidth: 200 }}
                                            />
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <TextField
                                                label="Question Appearance"
                                                value={field.questionAppearance || ''}
                                                onChange={(e) => updateField(stepIndex, fieldIndex, { questionAppearance: e.target.value })}
                                                size="small"
                                                fullWidth
                                                placeholder="Display name in Form Builder (optional)..."
                                                helperText="If set, this will be shown in Form Builder instead of the label"
                                            />
                                        </Box>
                                    </Paper>
                                ))}
                                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => addField(stepIndex)}>
                                    Add Field
                                </Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            <Button
                variant="outlined"
                fullWidth
                sx={{ border: '2px dashed #ccc', py: 2 }}
                onClick={addStep}
                startIcon={<AddIcon />}
            >
                Add New Step
            </Button>

            <GenerateQuestionnaireModal
                open={generateModalOpen}
                onClose={() => setGenerateModalOpen(false)}
                onGenerated={handleGeneratedDraft}
                isAdmin={true}
            />

            {/* Overwrite Confirmation Dialog */}
            <Dialog open={confirmOverwriteOpen} onClose={() => setConfirmOverwriteOpen(false)}>
                <DialogTitle>Replace Current Questionnaire?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Generating a questionnaire from a file will replace your currently loaded {definition.steps.length} step(s).
                        Are you sure you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmOverwriteOpen(false); setPendingDraft(null); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => { if (pendingDraft) applyDraft(pendingDraft); }}
                    >
                        Replace & Load Draft
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminWelcomeQuestionnaire;
